import { FC, ReactNode, useMemo, useRef, useState } from 'react';
import { OpaUser } from '@brg/api';
import { ExternalLink, FInter, FlexBox, MEDIA, Skeleton, useBreakpoint, useIsMobile, useScale } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import { DesignerCardLayoutWrapper, RatingModalWrapper, OpaCardText, SelectedWrapper, TooltipWrapper, TitleText } from './styles';
import styled, { css, useTheme } from 'styled-components';
import { LayoutConst, ScalableFlexBoxSquare, ScalableFlexBoxWidth, ScalableOPAFlexBoxSquare } from '../../../core';
import { SelectRing } from '../projectCard/projectCardNew';
import { SubscribeTools } from '../Subscribe/SubscribeTools';
import { CombinedEvents } from 'shared/helpers/projectEvents';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { IDesignerLight } from 'shared/common';
import { SUBSCRIBE_STATUS, SUBSCRIBE_VISIBILITY_STATUS } from '../Subscribe/const';
import { OverflowContainer } from '../projectCard/styles';
import { useLocation } from 'react-router-dom';
import { HiddenHolder, StopHolder } from '../SubscribeHolders/SubscribeHolders';
import { DesignerTags } from '../DesignerTags/DesignerTags';


interface RatingModalProps {
    finalRating: number;
    potentialRating: number;
    personalRating: number;
    personalPotentialRating: number;
    curatorRating: number;
    curatorPotentialRating: number;
    mediaRating: number;
    creatorRating: number;
    mediaPotentialRating: number;
}

const RatingModal: FC<RatingModalProps> = ({
    finalRating,
    personalRating,
    curatorRating,
    mediaRating,
    creatorRating,
}) => {
    const { t } = useTranslation();

    return (
        <RatingModalWrapper justify="center">
            <div className="left_col">
                <div>
                    {t('opa_table.mini_table.finalRating')} {finalRating}
                </div>
                <div>
                    {t('opa_table.mini_table.personalRating')} {personalRating}
                </div>
                <div>
                    {t('opa_table.mini_table.curatorRating')} {curatorRating}
                </div>
                <div>
                    {t('opa_table.mini_table.mediaRating')} {mediaRating}
                </div>
                <div>
                    {t('opa_table.mini_table.creatorRating')} {creatorRating}
                </div>
            </div>
        </RatingModalWrapper>
    );
};

export interface DesignerCardLayoutProps {
    isLoading?: boolean;
    avatar?: ReactNode;
    title?: ReactNode;
    visibility?: SUBSCRIBE_VISIBILITY_STATUS;
    subscribeStatus?: SUBSCRIBE_STATUS;
    text?: ReactNode;
    isMe?: any;
    designer?: IDesignerLight;
    onClick?: () => void;
    onSelect?: (id: string) => void;
    opaUser?: OpaUser;
    withRating?: boolean;
    more?: ReactNode;
    showOpa?: boolean;
    isSelectable?: boolean;
    alreadyProject?: boolean;
    selected?: boolean;
    identifier?: string;
    tooltip: any;
    handlers?: CombinedEvents;
    isGroupModal?: boolean;
    entityType?: string;
    twoMobileColumns?: boolean;
    isMobileMoreVisible?: boolean;
    href?: string;
    openInNewTab?: boolean;
}

export const DesignerCardLayout: FC<DesignerCardLayoutProps> = (props) => {
    const { isLoading, avatar, text, title, onClick, opaUser, showOpa, withRating = false } = props;
    const s = useScale();
    const [isRatingModal, setIsRatingModal] = useState(false);
    const theme = useTheme();

    const Rating = () => {
        if (!opaUser) {
            return <Skeleton height={20} width={25} />;
        }
        return (
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    setIsRatingModal(!isRatingModal);
                }}
                style={{ position: 'relative' }}
            >
                {opaUser.finalRating}
                {isRatingModal && (
                    <RatingModal
                        finalRating={opaUser.finalRating}
                        potentialRating={opaUser.potentialRating}
                        personalRating={opaUser.ratings[0].ratingSum}
                        personalPotentialRating={opaUser.ratings[0].potentialRatingSum}
                        curatorRating={opaUser.ratings[1].ratingSum}
                        curatorPotentialRating={opaUser.ratings[1].potentialRatingSum}
                        mediaRating={opaUser.ratings[2].ratingSum}
                        creatorRating={opaUser.ratings[4].ratingSum}
                        mediaPotentialRating={opaUser.ratings[2].potentialRatingSum}
                    />
                )}
            </div>
        );
    };

    return (
        <DesignerCardLayoutWrapper onClick={onClick} direction={'column'} width={s(270)} gy={s(16)}>
            <FlexBox width={s(270)} height={s(270)}>
                {isLoading ? (
                    <Skeleton
                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                        width={s(270)}
                        height={s(270)}
                    />
                ) : (
                    avatar
                )}
            </FlexBox>
            <FInter.SmallTextFlex direction={'column'} gy={8} color={theme.designerPage.textColor2}>
                <div>
                    {isLoading ? (
                        <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={180} />
                    ) : (
                        <FlexBox justify="space-between">
                            <div className={'dct'}>{title}</div>
                            {withRating && showOpa && <Rating />}
                        </FlexBox>
                    )}
                </div>
                <FInter.SmallTextDiv color={theme.designerPage.textColor2}>
                    {isLoading ? (
                        <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={30} width={270} />
                    ) : (
                        text
                    )}
                </FInter.SmallTextDiv>
            </FInter.SmallTextFlex>
        </DesignerCardLayoutWrapper>
    );
};

const DesignerCardRoot = styled.div<{ tooltipOpen?: boolean, isMobileMoreVisible?: boolean }>`
    cursor: pointer;
    position: relative;
    z-index: ${({ tooltipOpen }) => tooltipOpen ? 3 : 2};
    max-width: 100%;

    .hover-container {
        opacity: 0;
        transition: all 0.2s;
    }

    &:hover,
    &.hover-container--visible {
        cursor: pointer;
        opacity: 1;

        .hover-container{
            opacity: 1;
        }
    }

    @media (max-width: ${LayoutConst.screen.l}px) {
        .hover-container {
            opacity: 1;
            transition: all 0.2s;
        }
    }

    @media(max-width: ${LayoutConst.screen.m}px) {
    ${({ isMobileMoreVisible }) => isMobileMoreVisible && css`
        .hover-container {
            opacity: 1;
            transition: all 0.2s;
        }
    `};

    }

    @media${MEDIA.MOBILE} {
        width: 100%;

        a {
            width: 100%;
        }
    }
`

export const DesignerCardLayoutNew: FC<DesignerCardLayoutProps> = (props) => {
    const {
        isLoading,
        avatar,
        text,
        isMobileMoreVisible,
        title,
        tooltip,
        more,
        isMe,
        visibility,
        subscribeStatus,
        designer,
        onClick,
        isSelectable,
        alreadyProject,
        selected,
        onSelect,
        identifier,
        isGroupModal,
        twoMobileColumns,
        href,
        openInNewTab,
        entityType,
        ...handlers
    } = props;

    const theme = useTheme();
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
    const isTab = useBreakpoint(LayoutConst.screen.m);
    const isStopSubscribe = useMemo(() => {
        return subscribeStatus === SUBSCRIBE_STATUS.disable && isMe;
    }, [subscribeStatus, isMe])

    const isHiddenSubscribe = useMemo(() => {
        return visibility === SUBSCRIBE_VISIBILITY_STATUS.hidden && isMe;
    }, [visibility, isMe])

    const statusTags = useMemo(() => {
        if (entityType === 'statuses') {
            return designer?.responsibilityTags ?? [];
        }
        return [];
    }, [designer, entityType])

    const onContainerMouseLeave = () => {
        setTooltipOpen && setTooltipOpen(false);
    }

    const onContainerMouseEnter = () => {
        setTooltipOpen && setTooltipOpen(true)
    }

    const link = useMemo(() => {
        if (href) {
            return href
        }
        if (designer) {
            return CreateLinkTo.designerNew(designer)
        }
    }, [designer])

    const titleColor = useMemo(() => {
        if (entityType === 'statuses') {
            return theme.designerPage.textColor3;
        }
        return theme.designerPage.textColor2;
    }, [entityType, theme.designerPage.textColor2, theme.designerPage.textColor3]) as string;

    return (
        <DesignerCardRoot
            onMouseLeave={onContainerMouseLeave}
            onMouseEnter={onContainerMouseEnter}
            tooltipOpen={isTooltipOpen}
            isMobileMoreVisible={isMobileMoreVisible}
        >
            {/* @ts-ignore */}
            <ExternalLink to={link} external target={openInNewTab ? '_blank' : '_self'} rel={openInNewTab ? 'noopener noreferrer' : undefined}>
                <ScalableFlexBoxWidth
                    twoMobileColumns={twoMobileColumns}
                    isGroupModal={isGroupModal}
                    onClick={onClick}
                    direction={'column'}
                    directionMobile={'column'}
                    style={{ position: 'relative' }}
                    gy={16}
                >
                    <ScalableFlexBoxSquare style={{ position: 'relative' }} twoMobileColumns={twoMobileColumns} isGroupModal={isGroupModal}>
                        {tooltip && (
                            <FlexBox
                                style={{
                                    zIndex: 3,
                                    padding: isTab && isMobileMoreVisible ? '4px' : '16px 16px 0 0',
                                    ...(isTab && isMobileMoreVisible ? { position: 'absolute', top: 0, right: 0, maxWidth: '30px', maxHeight: '30px' } : {})
                                }}
                                {...(isTab && isMobileMoreVisible ? {
                                    onClick: (ev: React.BaseSyntheticEvent) => {
                                        ev.preventDefault();
                                        ev.stopPropagation();
                                    }
                                } : {})
                                }
                                width={'100%'}
                                height={'100%'}
                                justify='flex-end'
                                ref={tooltipRef}
                                className={'meta-container hover-container'}
                            >
                                <SubscribeTools
                                    identifier={identifier}
                                    visibility={visibility}
                                    status={subscribeStatus}
                                    designer={designer}
                                    {...handlers}
                                />
                            </FlexBox>

                        )}
                        <OverflowContainer lowOpacity={isHiddenSubscribe || isStopSubscribe}>
                            {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} /> : avatar}
                        </OverflowContainer>
                        {isHiddenSubscribe ? <HiddenHolder /> : isStopSubscribe ? <StopHolder /> : null}
                    </ScalableFlexBoxSquare>
                    <TitleText
                        direction={'column'}
                        directionMobile={'column'}
                        isLargeSize={entityType === 'statuses'}
                        gy={8}
                        color={titleColor}
                    >
                        <div>
                            {isLoading ? (
                                <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={180} />
                            ) : (
                                <FlexBox justify="space-between" justifyMobile={'space-between'}>
                                    <div className={'dct'}>{title}</div>
                                </FlexBox>
                            )}
                        </div>
                        <FInter.SmallTextDiv color={theme.designerPage.textColor2}>
                            {isLoading ? (
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    height={30}
                                    width={100}
                                />
                            ) : (
                                text
                            )}
                        </FInter.SmallTextDiv>
                    </TitleText>
                    {isSelectable && (
                        <SelectedWrapper className={'meta-container'}>
                            <>
                                <SelectRing active={selected} onClick={(ev) => {
                                    if (onSelect) {
                                        ev.stopPropagation();
                                        ev.preventDefault();
                                        onSelect(identifier ?? '');
                                    }
                                }}>
                                    {selected && (
                                        <div>
                                            <svg
                                                width="12"
                                                height="10"
                                                viewBox="0 0 12 10"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M1 4.85653L4.62216 8.45739L11.2699 1.83097L10.4389 1L4.62216 6.79545L1.80966
                                                4.02557L1 4.85653Z"
                                                    fill="#FEFEFE"
                                                    stroke="black"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </SelectRing>
                            </>
                        </SelectedWrapper>
                    )}
                </ScalableFlexBoxWidth>
            </ExternalLink>
            {statusTags?.length > 0 && (
                <DesignerTags tags={statusTags} />
            )}
        </DesignerCardRoot>
    );
};



export const DesignerOpaCardLayoutNew: FC<DesignerCardLayoutProps> = (props) => {
    const { isLoading, avatar, text, title, onClick, designer, isGroupModal, href, openInNewTab } = props;
    const theme = useTheme();
    const isMobile = useIsMobile();
    const location = useLocation();
    const hasEmployeesRoute = location.pathname.includes('/employees');

    const content = (
        <DesignerCardRoot>
            <ExternalLink to={href} external target={openInNewTab ? '_blank' : '_self'} rel={openInNewTab ? 'noopener noreferrer' : undefined}>
                <FlexBox onClick={onClick} direction={'column'} directionMobile={'column'} gy={isMobile ? 12 : 16}>
                    <ScalableOPAFlexBoxSquare>
                        {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} /> : avatar}
                    </ScalableOPAFlexBoxSquare>
                    <FInter.TextBoldFlex
                        direction={'column'}
                        directionMobile={'column'}
                        gy={8}
                        color={theme.designerPage.textColor2}
                    >
                        <div>
                            {isLoading ? (
                                <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={16} width={'73%'} />
                            ) : (
                                <FlexBox justify="space-between" justifyMobile={'space-between'}>
                                    <OpaCardText className={'dct'} style={{ color: theme.designerPage.textColor3 }}>{title}</OpaCardText>
                                </FlexBox>
                            )}
                        </div>

                        {text && (
                            <FInter.SmallTextDiv color={theme.designerPage.textColor2}>
                                {isLoading ? (
                                    <Skeleton
                                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                        height={30}
                                        width={'100$'}
                                    />
                                ) : (
                                    text
                                )}
                            </FInter.SmallTextDiv>
                        )}
                    </FInter.TextBoldFlex>
                </FlexBox>
            </ExternalLink>
        </DesignerCardRoot>
    );

    return content
};
import { FC, ReactNode, useMemo, useRef, useState } from 'react';
import { OpaUser } from '@brg/api';
import { ExternalLink, FInter, FlexBox, MEDIA, Skeleton, useBreakpoint, useIsMobile, useScale } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import { DesignerCardLayoutWrapper, RatingModalWrapper, OpaCardText, SelectedWrapper, TooltipWrapper, TitleText } from './styles';
import styled, { css, useTheme } from 'styled-components';
import { LayoutConst, ScalableFlexBoxSquare, ScalableFlexBoxWidth, ScalableOPAFlexBoxSquare } from '../../../core';
import { SelectRing } from '../projectCard/projectCardNew';
import { SubscribeTools } from '../Subscribe/SubscribeTools';
import { CombinedEvents } from 'shared/helpers/projectEvents';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { IDesignerLight } from 'shared/common';
import { SUBSCRIBE_STATUS, SUBSCRIBE_VISIBILITY_STATUS } from '../Subscribe/const';
import { OverflowContainer } from '../projectCard/styles';
import { useLocation } from 'react-router-dom';
import { HiddenHolder, StopHolder } from '../SubscribeHolders/SubscribeHolders';
import { DesignerTags } from '../DesignerTags/DesignerTags';


interface RatingModalProps {
    finalRating: number;
    potentialRating: number;
    personalRating: number;
    personalPotentialRating: number;
    curatorRating: number;
    curatorPotentialRating: number;
    mediaRating: number;
    creatorRating: number;
    mediaPotentialRating: number;
}

const RatingModal: FC<RatingModalProps> = ({
    finalRating,
    personalRating,
    curatorRating,
    mediaRating,
    creatorRating,
}) => {
    const { t } = useTranslation();

    return (
        <RatingModalWrapper justify="center">
            <div className="left_col">
                <div>
                    {t('opa_table.mini_table.finalRating')} {finalRating}
                </div>
                <div>
                    {t('opa_table.mini_table.personalRating')} {personalRating}
                </div>
                <div>
                    {t('opa_table.mini_table.curatorRating')} {curatorRating}
                </div>
                <div>
                    {t('opa_table.mini_table.mediaRating')} {mediaRating}
                </div>
                <div>
                    {t('opa_table.mini_table.creatorRating')} {creatorRating}
                </div>
            </div>
        </RatingModalWrapper>
    );
};

export interface DesignerCardLayoutProps {
    isLoading?: boolean;
    avatar?: ReactNode;
    title?: ReactNode;
    visibility?: SUBSCRIBE_VISIBILITY_STATUS;
    subscribeStatus?: SUBSCRIBE_STATUS;
    text?: ReactNode;
    isMe?: any;
    designer?: IDesignerLight;
    onClick?: () => void;
    onSelect?: (id: string) => void;
    opaUser?: OpaUser;
    withRating?: boolean;
    more?: ReactNode;
    showOpa?: boolean;
    isSelectable?: boolean;
    alreadyProject?: boolean;
    selected?: boolean;
    identifier?: string;
    tooltip: any;
    handlers?: CombinedEvents;
    isGroupModal?: boolean;
    entityType?: string;
    twoMobileColumns?: boolean;
    isMobileMoreVisible?: boolean;
    href?: string;
    openInNewTab?: boolean;
}

export const DesignerCardLayout: FC<DesignerCardLayoutProps> = (props) => {
    const { isLoading, avatar, text, title, onClick, opaUser, showOpa, withRating = false } = props;
    const s = useScale();
    const [isRatingModal, setIsRatingModal] = useState(false);
    const theme = useTheme();

    const Rating = () => {
        if (!opaUser) {
            return <Skeleton height={20} width={25} />;
        }
        return (
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    setIsRatingModal(!isRatingModal);
                }}
                style={{ position: 'relative' }}
            >
                {opaUser.finalRating}
                {isRatingModal && (
                    <RatingModal
                        finalRating={opaUser.finalRating}
                        potentialRating={opaUser.potentialRating}
                        personalRating={opaUser.ratings[0].ratingSum}
                        personalPotentialRating={opaUser.ratings[0].potentialRatingSum}
                        curatorRating={opaUser.ratings[1].ratingSum}
                        curatorPotentialRating={opaUser.ratings[1].potentialRatingSum}
                        mediaRating={opaUser.ratings[2].ratingSum}
                        creatorRating={opaUser.ratings[4].ratingSum}
                        mediaPotentialRating={opaUser.ratings[2].potentialRatingSum}
                    />
                )}
            </div>
        );
    };

    return (
        <DesignerCardLayoutWrapper onClick={onClick} direction={'column'} width={s(270)} gy={s(16)}>
            <FlexBox width={s(270)} height={s(270)}>
                {isLoading ? (
                    <Skeleton
                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                        width={s(270)}
                        height={s(270)}
                    />
                ) : (
                    avatar
                )}
            </FlexBox>
            <FInter.SmallTextFlex direction={'column'} gy={8} color={theme.designerPage.textColor2}>
                <div>
                    {isLoading ? (
                        <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={180} />
                    ) : (
                        <FlexBox justify="space-between">
                            <div className={'dct'}>{title}</div>
                            {withRating && showOpa && <Rating />}
                        </FlexBox>
                    )}
                </div>
                <FInter.SmallTextDiv color={theme.designerPage.textColor2}>
                    {isLoading ? (
                        <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={30} width={270} />
                    ) : (
                        text
                    )}
                </FInter.SmallTextDiv>
            </FInter.SmallTextFlex>
        </DesignerCardLayoutWrapper>
    );
};

const DesignerCardRoot = styled.div<{ tooltipOpen?: boolean, isMobileMoreVisible?: boolean }>`
    cursor: pointer;
    position: relative;
    z-index: ${({ tooltipOpen }) => tooltipOpen ? 3 : 2};
    max-width: 100%;

    .hover-container {
        opacity: 0;
        transition: all 0.2s;
    }

    &:hover,
    &.hover-container--visible {
        cursor: pointer;
        opacity: 1;

        .hover-container{
            opacity: 1;
        }
    }

    @media (max-width: ${LayoutConst.screen.l}px) {
        .hover-container {
            opacity: 1;
            transition: all 0.2s;
        }
    }

    @media(max-width: ${LayoutConst.screen.m}px) {
    ${({ isMobileMoreVisible }) => isMobileMoreVisible && css`
        .hover-container {
            opacity: 1;
            transition: all 0.2s;
        }
    `};

    }

    @media${MEDIA.MOBILE} {
        width: 100%;

        a {
            width: 100%;
        }
    }
`

export const DesignerCardLayoutNew: FC<DesignerCardLayoutProps> = (props) => {
    const {
        isLoading,
        avatar,
        text,
        isMobileMoreVisible,
        title,
        tooltip,
        more,
        isMe,
        visibility,
        subscribeStatus,
        designer,
        onClick,
        isSelectable,
        alreadyProject,
        selected,
        onSelect,
        identifier,
        isGroupModal,
        twoMobileColumns,
        href,
        openInNewTab,
        entityType,
        ...handlers
    } = props;

    const theme = useTheme();
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
    const isTab = useBreakpoint(LayoutConst.screen.m);
    const isStopSubscribe = useMemo(() => {
        return subscribeStatus === SUBSCRIBE_STATUS.disable && isMe;
    }, [subscribeStatus, isMe])

    const isHiddenSubscribe = useMemo(() => {
        return visibility === SUBSCRIBE_VISIBILITY_STATUS.hidden && isMe;
    }, [visibility, isMe])

    const statusTags = useMemo(() => {
        if (entityType === 'statuses') {
            return designer?.responsibilityTags ?? [];
        }
        return [];
    }, [designer, entityType])

    const onContainerMouseLeave = () => {
        setTooltipOpen && setTooltipOpen(false);
    }

    const onContainerMouseEnter = () => {
        setTooltipOpen && setTooltipOpen(true)
    }

    const link = useMemo(() => {
        if (href) {
            return href
        }
        if (designer) {
            return CreateLinkTo.designerNew(designer)
        }
    }, [designer])

    const titleColor = useMemo(() => {
        if (entityType === 'statuses') {
            return theme.designerPage.textColor3;
        }
        return theme.designerPage.textColor2;
    }, [entityType, theme.designerPage.textColor2, theme.designerPage.textColor3]) as string;

    return (
        <DesignerCardRoot
            onMouseLeave={onContainerMouseLeave}
            onMouseEnter={onContainerMouseEnter}
            tooltipOpen={isTooltipOpen}
            isMobileMoreVisible={isMobileMoreVisible}
        >
            {/* @ts-ignore */}
            <ExternalLink to={link} external target={openInNewTab ? '_blank' : '_self'} rel={openInNewTab ? 'noopener noreferrer' : undefined}>
                <ScalableFlexBoxWidth
                    twoMobileColumns={twoMobileColumns}
                    isGroupModal={isGroupModal}
                    onClick={onClick}
                    direction={'column'}
                    directionMobile={'column'}
                    style={{ position: 'relative' }}
                    gy={16}
                >
                    <ScalableFlexBoxSquare style={{ position: 'relative' }} twoMobileColumns={twoMobileColumns} isGroupModal={isGroupModal}>
                        {tooltip && (
                            <FlexBox
                                style={{
                                    zIndex: 3,
                                    padding: isTab && isMobileMoreVisible ? '4px' : '16px 16px 0 0',
                                    ...(isTab && isMobileMoreVisible ? { position: 'absolute', top: 0, right: 0, maxWidth: '30px', maxHeight: '30px' } : {})
                                }}
                                {...(isTab && isMobileMoreVisible ? {
                                    onClick: (ev: React.BaseSyntheticEvent) => {
                                        ev.preventDefault();
                                        ev.stopPropagation();
                                    }
                                } : {})
                                }
                                width={'100%'}
                                height={'100%'}
                                justify='flex-end'
                                ref={tooltipRef}
                                className={'meta-container hover-container'}
                            >
                                <SubscribeTools
                                    identifier={identifier}
                                    visibility={visibility}
                                    status={subscribeStatus}
                                    designer={designer}
                                    {...handlers}
                                />
                            </FlexBox>

                        )}
                        <OverflowContainer lowOpacity={isHiddenSubscribe || isStopSubscribe}>
                            {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} /> : avatar}
                        </OverflowContainer>
                        {isHiddenSubscribe ? <HiddenHolder /> : isStopSubscribe ? <StopHolder /> : null}
                    </ScalableFlexBoxSquare>
                    <TitleText
                        direction={'column'}
                        directionMobile={'column'}
                        isLargeSize={entityType === 'statuses'}
                        gy={8}
                        color={titleColor}
                    >
                        <div>
                            {isLoading ? (
                                <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={180} />
                            ) : (
                                <FlexBox justify="space-between" justifyMobile={'space-between'}>
                                    <div className={'dct'}>{title}</div>
                                </FlexBox>
                            )}
                        </div>
                        <FInter.SmallTextDiv color={theme.designerPage.textColor2}>
                            {isLoading ? (
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    height={30}
                                    width={100}
                                />
                            ) : (
                                text
                            )}
                        </FInter.SmallTextDiv>
                    </TitleText>
                    {isSelectable && (
                        <SelectedWrapper className={'meta-container'}>
                            <>
                                <SelectRing active={selected} onClick={(ev) => {
                                    if (onSelect) {
                                        ev.stopPropagation();
                                        ev.preventDefault();
                                        onSelect(identifier ?? '');
                                    }
                                }}>
                                    {selected && (
                                        <div>
                                            <svg
                                                width="12"
                                                height="10"
                                                viewBox="0 0 12 10"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M1 4.85653L4.62216 8.45739L11.2699 1.83097L10.4389 1L4.62216 6.79545L1.80966
                                                4.02557L1 4.85653Z"
                                                    fill="#FEFEFE"
                                                    stroke="black"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </SelectRing>
                            </>
                        </SelectedWrapper>
                    )}
                </ScalableFlexBoxWidth>
            </ExternalLink>
            {statusTags?.length > 0 && (
                <DesignerTags tags={statusTags} />
            )}
        </DesignerCardRoot>
    );
};



export const DesignerOpaCardLayoutNew: FC<DesignerCardLayoutProps> = (props) => {
    const { isLoading, avatar, text, title, onClick, designer, isGroupModal, href, openInNewTab } = props;
    const theme = useTheme();
    const isMobile = useIsMobile();
    const location = useLocation();
    const hasEmployeesRoute = location.pathname.includes('/employees');

    const content = (
        <DesignerCardRoot>
            <ExternalLink to={href} external target={openInNewTab ? '_blank' : '_self'} rel={openInNewTab ? 'noopener noreferrer' : undefined}>
                <FlexBox onClick={onClick} direction={'column'} directionMobile={'column'} gy={isMobile ? 12 : 16}>
                    <ScalableOPAFlexBoxSquare>
                        {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} /> : avatar}
                    </ScalableOPAFlexBoxSquare>
                    <FInter.TextBoldFlex
                        direction={'column'}
                        directionMobile={'column'}
                        gy={8}
                        color={theme.designerPage.textColor2}
                    >
                        <div>
                            {isLoading ? (
                                <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={16} width={'73%'} />
                            ) : (
                                <FlexBox justify="space-between" justifyMobile={'space-between'}>
                                    <OpaCardText className={'dct'} style={{ color: theme.designerPage.textColor3 }}>{title}</OpaCardText>
                                </FlexBox>
                            )}
                        </div>

                        {text && (
                            <FInter.SmallTextDiv color={theme.designerPage.textColor2}>
                                {isLoading ? (
                                    <Skeleton
                                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                        height={30}
                                        width={'100$'}
                                    />
                                ) : (
                                    text
                                )}
                            </FInter.SmallTextDiv>
                        )}
                    </FInter.TextBoldFlex>
                </FlexBox>
            </ExternalLink>
        </DesignerCardRoot>
    );

    return content
};
